import React from "react";
import { notification, Modal } from "antd";
import messages from "../../errors/messages.en-IN.json";

/**
 * BUG-2570: one place for every "did not print" message in the POS.
 *
 * Every print (bill, KOT, estimate, Close Till report, ...) is fire-and-forget: the
 * sale / order is already saved when the print is sent, and a print failure never
 * reaches the screen that started it. Each print path used to show its own text, or
 * nothing at all. Now every failure is described by two things:
 *
 *   kind  - what did not print (messages.print.kinds): its label and what the cashier
 *           does next (where to reprint, or that there is no reprint)
 *   cause - why it did not print (messages.print.causes): a plain reason, the steps and a
 *           short label for the grouped view
 *
 * and is shown as (UX review v2, Sudheer B):
 *   title   "<Kind> <ref> not printed"   (KOT: "KOT not printed — <station>, <table>")
 *   detail  "<Reason> <steps> <next>"
 *   line    "<Kind> <ref> — <short>"      (one line per print when several failed)
 *
 * Billing is never blocked: bill / KOT / estimate failures go to one notice bar
 * (PrintFailureBar) that sits inline in the payment panel (retail) or above the table grid
 * (restaurant), groups several failures and collapses to a small icon after 15 seconds.
 * When no payment panel / table grid is on screen, the floating bar mounted in App.js shows
 * it instead. Only the Close Till and day end reports use a popup (POPUP_KINDS): the till
 * logs out and reloads right after them, so a bar would be lost.
 */

// Read on use, not at import: many tests mock the messages file without a print section.
const printText = () => messages?.print || {};
const FALLBACK_KIND = { label: "Print", next: "Print it again after fixing this." };
const FALLBACK_CAUSE = { reason: "The printer is not connected.", steps: "Restart the computer.", short: "printer not connected" };

// The till logs out (full page reload) right after these, so the cashier must see an OK popup.
export const POPUP_KINDS = new Set(["tillClose", "dayEnd"]);

// Named "KOT not printed — <station>, <table>" (the station / table is what the cashier looks for).
const DASH_TITLE_KINDS = new Set(["kot", "cancelKot"]);

// How long Close Till / day end wait for the printer's answer before going on to log out.
export const PRINT_WAIT_MS = 8000;

// Custom axios config keys (axios keeps unknown keys on error.config): tag a print request
// with what it prints so the global notifier can name it. Spread into the axios config.
// displayRef: what the cashier reads, when the ref carries an id only there to keep prints apart.
const asRef = (ref) => (ref === undefined || ref === null ? "" : String(ref));
export const printTag = (kind, ref, displayRef) => ({
  printKind: kind,
  printRef: asRef(ref),
  ...(displayRef !== undefined && displayRef !== null ? { printDisplayRef: String(displayRef) } : {}),
});

// A KOT is named by station, table and KOT token / order number: the notice groups by kind + ref, so
// a ref of the station alone merged the second order's failed KOT into the first (HMRES01 staging).
export const kotPrintRef = (...parts) =>
  parts
    .map((p) => (p === undefined || p === null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

// [ref, displayRef]: the LAST part (KOT token / order id) keeps two KOTs apart but means nothing to
// the cashier, so the text shows the other parts only (v2: "KOT not printed — Banjara Main Course, T-9").
export const kotPrintRefs = (...parts) => [kotPrintRef(...parts), kotPrintRef(...parts.slice(0, -1))];

export const buildPrintFailureText = ({ kind, ref, displayRef, cause, label }) => {
  const kinds = printText().kinds || {};
  const causes = printText().causes || {};
  const k = kinds[kind] || kinds.print || FALLBACK_KIND;
  const c = causes[cause] || causes.notConnected || FALLBACK_CAUSE;
  const name = label || k.label;
  const shown = displayRef || ref || "";
  const named = `${name}${shown ? ` ${shown}` : ""}`;
  const title = DASH_TITLE_KINDS.has(kind) && shown ? `${name} not printed — ${shown}` : `${named} not printed`;
  const detail = [c.reason, c.steps, k.next].filter(Boolean).join(" ");
  const line = `${named} — ${c.short || c.reason}`;
  return { title, detail, line };
};

// Chrome's Local Network Access permission (Chrome 142+ blocks a website from calling
// 127.0.0.1 / the LAN until the cashier allows it). Newer builds split it into
// loopback-network (127.0.0.1) / local-network (a LAN printer, e.g. OFC 10.x:8090). Unsupported
// names throw, so each is tried in turn, the one for the printer's own address first.
const LNA_PERMISSION_NAMES = ["local-network-access", "loopback-network", "local-network"];
const LAN_FIRST = ["local-network-access", "local-network", "loopback-network"];
const isLoopbackUrl = (url) => /^https?:\/\/(127\.|localhost|\[::1\])/i.test(url || "");
export const getLocalNetworkPermission = async (url) => {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) return null;
  for (const name of !url || isLoopbackUrl(url) ? LNA_PERMISSION_NAMES : LAN_FIRST) {
    try {
      const status = await navigator.permissions.query({ name });
      if (status?.state) return status.state;
    } catch {
      // this browser does not know this permission name; try the next one
    }
  }
  return null;
};

// error: the axios (or bridge) error of a failed print request; null for a request that
// never answered within the wait.
export const classifyPrintError = async (error) => {
  const url = error?.config?.url || "";
  if (/^(undefined|null)?printer/.test(url)) return "noPrinterForTill";
  // The print service answered with an error status: it is running, the printer is not.
  if (error?.response) return "printerError";
  // CW Browser prints through its own shell: there is no browser permission to allow.
  if (typeof window !== "undefined" && window.electronAPI) return "serviceDown";
  const permission = await getLocalNetworkPermission(url);
  if (permission === "denied" || permission === "prompt") return "browserBlocked";
  if (permission === "granted") return "serviceDown";
  // The browser cannot say which one: name both steps.
  return "notConnected";
};

// ── Notice bar (billing never blocked) ─────────────────────────────────────────

const NOTICE_KEY = "print-failures";
// Fallback only (no PrintFailureBar mounted): not topRight, there it covers the payment tiles.
export const NOTICE_PLACEMENT = "bottomLeft";
// The bar folds to a small icon after this long, so it never stays over the payment tiles.
export const COLLAPSE_MS = 15000;
// Several failures: show the newest lines only, so the bar never covers the screen.
export const MAX_LINES = 2;

const openFailures = new Map();
const listeners = new Set();
let collapseTimer = null;
let mountedHosts = 0;
let snapshot = { entries: [], collapsed: false, collapseAt: 0, inlineHosts: 0 };

const emit = (patch) => {
  snapshot = { ...snapshot, entries: [...openFailures.values()], ...patch };
  listeners.forEach((l) => l());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => snapshot;

const startCollapseTimer = () => {
  clearTimeout(collapseTimer);
  collapseTimer = setTimeout(() => emit({ collapsed: true, collapseAt: 0 }), COLLAPSE_MS);
  return Date.now() + COLLAPSE_MS;
};

export const expandPrintFailures = () => {
  if (openFailures.size === 0) return;
  emit({ collapsed: false, collapseAt: startCollapseTimer() });
};

export const getPrintFailureState = () => snapshot;

const groupFooter = (entries, seconds) => {
  const parts = [];
  if (entries.length > MAX_LINES) parts.push((printText().group_more || "and {count} more").replace("{count}", String(entries.length - MAX_LINES)));
  if (seconds > 0) parts.push((printText().collapses_in || "collapses in {seconds}s").replace("{seconds}", String(seconds)));
  return parts.join(" · ");
};

const groupTitle = (entries) => (printText().group_title || "{count} prints did not print").replace("{count}", String(entries.length));

const NoticeBody = ({ entries, seconds }) => {
  const single = entries.length === 1;
  const footer = groupFooter(entries, seconds);
  return (
    <div data-testid="print-failure-notice">
      {single ? (
        <div data-testid="print-failure-detail">{entries[0].detail}</div>
      ) : (
        entries.slice(-MAX_LINES).map((e) => (
          <div key={e.key} data-testid="print-failure-line">
            {e.line}
          </div>
        ))
      )}
      {footer && (
        <div data-testid="print-failure-footer" style={{ marginTop: 4, fontSize: 12, color: "#8c8c8c" }}>
          {footer}
        </div>
      )}
    </div>
  );
};

// No PrintFailureBar on screen (outside the app shell): the antd corner notification.
const renderFallbackNotice = () => {
  const entries = [...openFailures.values()];
  if (entries.length === 0) return;
  try {
    notification.error({
      key: NOTICE_KEY,
      message: entries.length === 1 ? entries[0].title : groupTitle(entries),
      description: <NoticeBody entries={entries} seconds={0} />,
      duration: 0,
      placement: NOTICE_PLACEMENT,
      onClose: () => clearPrintFailures(),
    });
  } catch {
    // notification unavailable (outside the React tree); the console line still records it
  }
};

export const getOpenPrintFailures = () => [...openFailures.values()];
export const clearPrintFailures = () => {
  openFailures.clear();
  clearTimeout(collapseTimer);
  emit({ collapsed: false, collapseAt: 0 });
  try {
    notification.destroy(NOTICE_KEY);
  } catch {
    // nothing open
  }
};

const useSecondsLeft = (collapseAt) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!collapseAt) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [collapseAt]);
  return collapseAt ? Math.max(0, Math.ceil((collapseAt - now) / 1000)) : 0;
};

const BAR_STYLE = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  background: "#fff1f0",
  border: "1px solid #ffa39e",
  borderLeft: "4px solid #cf1322",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  color: "#2F3856",
  textAlign: "left",
};
const FLOATING_STYLE = { position: "fixed", left: 16, bottom: 16, zIndex: 1010, maxWidth: 420, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" };

/**
 * placement "inline": inside a payment panel / above the table grid; shown while `active`.
 * placement "floating": the App.js host; shown only when no inline bar is on screen.
 */
export const PrintFailureBar = ({ placement = "inline", active = true, style }) => {
  const state = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const inline = placement === "inline";
  const seconds = useSecondsLeft(state.collapsed ? 0 : state.collapseAt);

  React.useEffect(() => {
    mountedHosts += 1;
    return () => {
      mountedHosts -= 1;
    };
  }, []);
  React.useEffect(() => {
    if (!inline || !active) return undefined;
    emit({ inlineHosts: snapshot.inlineHosts + 1 });
    return () => emit({ inlineHosts: Math.max(0, snapshot.inlineHosts - 1) });
  }, [inline, active]);

  const { entries } = state;
  if (entries.length === 0) return null;
  if (inline && !active) return null;
  if (!inline && state.inlineHosts > 0) return null;
  const frame = inline ? { margin: "0 0 8px", ...style } : { ...FLOATING_STYLE, ...style };

  if (state.collapsed) {
    return (
      <button
        type="button"
        data-testid="print-failure-collapsed"
        data-placement={placement}
        onClick={expandPrintFailures}
        title={entries.length === 1 ? entries[0].title : groupTitle(entries)}
        style={{ ...frame, ...BAR_STYLE, display: "inline-flex", alignItems: "center", cursor: "pointer", padding: "4px 10px" }}
      >
        <span style={{ color: "#cf1322", fontWeight: 700 }}>⊘</span>
        <span>{(printText().collapsed_label || "{count} not printed").replace("{count}", String(entries.length))}</span>
      </button>
    );
  }

  return (
    <div role="alert" data-testid="print-failure-bar" data-placement={placement} style={{ ...frame, ...BAR_STYLE }}>
      <span style={{ color: "#cf1322", fontWeight: 700, fontSize: 16, lineHeight: "18px" }}>⊘</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b data-testid="print-failure-title">{entries.length === 1 ? entries[0].title : groupTitle(entries)}</b>
        <NoticeBody entries={entries} seconds={seconds} />
      </div>
      <button
        type="button"
        aria-label={printText().close || "Close"}
        data-testid="print-failure-close"
        onClick={clearPrintFailures}
        style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 16, color: "#8c8c8c", lineHeight: "16px" }}
      >
        ×
      </button>
    </div>
  );
};

const logFailure = (text, detail) => console.error(`[PrintFailure] ${text.title}. ${text.detail}`, detail || "");

// Popup for Close Till / day end. Resolves when the cashier presses OK.
export const showPrintFailurePopup = ({ kind, ref, displayRef, cause, label, detail }) => {
  const text = buildPrintFailureText({ kind, ref, displayRef, cause, label });
  logFailure(text, detail);
  return new Promise((resolve) => {
    try {
      Modal.error({
        title: text.title,
        content: (
          <div data-testid="print-failure-popup">
            <div>{text.detail}</div>
          </div>
        ),
        okText: "OK",
        centered: true,
        onOk: () => resolve(),
        onCancel: () => resolve(),
      });
    } catch {
      resolve();
    }
  });
};

// Every failed print ends here. Same kind + ref + cause shows once (copies, per-station loops).
export const showPrintFailure = ({ kind = "print", ref = "", displayRef, cause, label, detail }) => {
  if (POPUP_KINDS.has(kind)) return showPrintFailurePopup({ kind, ref, displayRef, cause, label, detail });
  const text = buildPrintFailureText({ kind, ref, displayRef, cause, label });
  const key = `${kind}|${ref}|${cause}|${label || ""}`;
  if (openFailures.has(key)) return Promise.resolve();
  logFailure(text, detail);
  openFailures.set(key, { key, kind, ref, cause, ...text });
  // A new failure always opens the bar again and restarts the 15 s fold.
  emit({ collapsed: false, collapseAt: startCollapseTimer() });
  if (mountedHosts === 0) renderFallbackNotice();
  return Promise.resolve();
};

// A failed print request (axios error, or null when it never answered).
export const reportPrintError = async ({ kind, ref, displayRef, label, error }) => {
  const cause = await classifyPrintError(error);
  return showPrintFailure({ kind, ref, displayRef, cause, label, detail: error?.message || "no answer" });
};

// Close Till / day end: wait (capped) for the printer's answer. Never rejects.
export const waitForPrint = (promise, ms = PRINT_WAIT_MS) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, error: null, timedOut: true }), ms);
    Promise.resolve(promise).then(
      (response) => {
        clearTimeout(timer);
        resolve({ ok: true, response });
      },
      (error) => {
        clearTimeout(timer);
        resolve({ ok: false, error });
      }
    );
  });

// Close Till / day end print: send, wait for the answer (capped), and on failure show the
// popup and wait for OK before the caller goes on to log out.
export const printAndConfirm = async (sendPrint, { kind, ref, label } = {}) => {
  let request;
  try {
    request = sendPrint();
  } catch (error) {
    request = Promise.reject(error);
  }
  const result = await waitForPrint(request);
  if (result.ok) return result;
  await reportPrintError({ kind, ref, label, error: result.error });
  return result;
};
